import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useI18n } from '../i18n/i18nContext';
import { userAPI } from '../services/api';
import LocalizedLink from './LocalizedLink';
import BirthdayPicker from './BirthdayPicker';

const CompleteRegistration: React.FC = () => {
  const { user } = useAuth0();
  const navigate = useLocalizedNavigate();
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [gender, setGender] = useState<'male' | 'female' | ''>('');
  const [birthday, setBirthday] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [promoPreference, setPromoPreference] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [birthdayError, setBirthdayError] = useState('');

  // Check if user has already completed registration
  useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        const profile = await userAPI.getProfile();
        if (profile.data.registrationCompleted) {
          // User has already completed registration
          const returnTo = localStorage.getItem('registration_return_to');
          localStorage.removeItem('registration_return_to');
          navigate(returnTo || '/account');
          return;
        }
        // Pre-fill name from database profile if available
        if (profile.data.name && !profile.data.name.includes('@')) {
          setName(profile.data.name);
        }
      } catch (error) {
        console.error('Error checking registration status:', error);
      } finally {
        setIsCheckingStatus(false);
      }
    };

    checkRegistrationStatus();
  }, [navigate]);

  // Initialize name from Auth0 user data if not already set from profile
  useEffect(() => {
    if (!name && user?.name) {
      // Do not pre-fill if the name from Auth0 is an email address
      if (!user.name.includes('@')) {
        setName(user.name);
      }
    }
  }, [user, name]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      alert(t.registration.mustAgreeToTerms);
      return;
    }

    if (!name.trim()) {
      alert(t.registration.nameRequired);
      return;
    }

    // Validate birthday if provided
    if (birthday) {
      const birthdayDate = new Date(birthday);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (birthdayDate > today) {
        setBirthdayError(t.registration.birthdayInFuture);
        return;
      }
    }

    setIsSubmitting(true);
    
    try {
      // Save user preferences to backend via API
      await userAPI.completeRegistration({
        name: name.trim(),
        termsAccepted: agreedToTerms,
        promotionalEmails: promoPreference,
        gender: gender || undefined,
        birthday: birthday || undefined
      });

      // Keep localStorage for immediate UI feedback
      localStorage.setItem('just_registered', 'true');
      
      // Check if there's a return path stored
      const returnTo = localStorage.getItem('registration_return_to');
      localStorage.removeItem('registration_return_to');
      localStorage.removeItem('auth_return_to');
      
      // Redirect to the stored path or account page
      navigate(returnTo || '/account');
    } catch (error) {
      console.error(t.console.errors.completingRegistration, error);
      alert(t.registration.errorOccurred);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state while checking registration status
  if (isCheckingStatus) {
    return (
      <div className="bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {t.registration.oneLastStep}
        </h2>
        <p className="mt-4 text-center text-lg text-gray-600">
          {t.registration.welcome}, {user?.name || user?.email}!
        </p>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t.registration.reviewTerms}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                {t.registration.nameLabel}
                <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder={t.registration.namePlaceholder}
              />
            </div>

            <div>
              <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
                {t.registration.gender}
              </label>
              <select
                id="gender"
                name="gender"
                value={gender}
                onChange={(e) => setGender(e.target.value as 'male' | 'female' | '')}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">{t.registration.genderPlaceholder}</option>
                <option value="male">{t.registration.male}</option>
                <option value="female">{t.registration.female}</option>
              </select>
            </div>

            <div>
              <label htmlFor="birthday" className="block text-sm font-medium text-gray-700">
                {t.registration.birthday}
              </label>
              <BirthdayPicker
                value={birthday}
                onChange={(value) => {
                  setBirthday(value);
                  setBirthdayError('');
                }}
                error={birthdayError}
                className="mt-1"
              />
            </div>

            <div>
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="terms"
                    name="terms"
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    required
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="terms" className="font-medium text-gray-700">
                    {t.registration.agreeToTerms}{' '}
                    <LocalizedLink
                      to="/p/terms"
                      target="_blank"
                      className="text-blue-600 hover:text-blue-500"
                    >
                      {t.registration.termsOfService}
                    </LocalizedLink>
                    <span className="text-red-500">*</span>
                  </label>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="promoPreference"
                    name="promoPreference"
                    type="checkbox"
                    checked={promoPreference}
                    onChange={(e) => setPromoPreference(e.target.checked)}
                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="promoPreference" className="font-medium text-gray-700">
                    {t.registration.promoOptIn}
                  </label>
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting || !agreedToTerms}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  agreedToTerms && !isSubmitting
                    ? 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? t.registration.completing : t.registration.completeSignUp}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompleteRegistration;